import React, { useState } from 'react';
import {
  BookOpen,
  CheckCircle,
  XCircle,
  Plus,
  Edit,
  Trash2,
  Loader2,
  ListChecks,
} from 'lucide-react';
import { Button, SearchSelect } from '../../components/shared';
import DataTable from '../../components/shared/DataTable';
import PillToggle from '../../components/shared/PillToggle';
import AdminFAQModal from '../../components/admin/AdminFAQModal';
import { useAdminFAQ } from '../../hooks/useAdminFAQ';
import { formatDate } from '../../utils/dateUtils';
import { AdminPageHeader, AdminGuideCallout } from '../../components/admin/guidance';
import { ADMIN_PAGE_GUIDES } from '../../utils/adminPageGuides';

const formatCategory = (category) =>
  (category || 'general').replace(/-/g, ' ').replace(/\b\w/g, (c) => c.toUpperCase());

const AdminFAQ = () => {
  const [showModal, setShowModal] = useState(false);
  const [editingFAQ, setEditingFAQ] = useState(null);
  const [togglingId, setTogglingId] = useState(null);

  const {
    faqs,
    isLoading,
    error,
    filters,
    activeStatusOptions,
    handleFilterChange,
    handleSearch,
    statistics: stats,
    toggleStatus,
    isToggling,
    deleteFAQ,
    createFAQ,
    isCreating,
    updateFAQ,
    isUpdating,
  } = useAdminFAQ();

  const openCreate = () => {
    setEditingFAQ(null);
    setShowModal(true);
  };

  const openEdit = (faq) => {
    setEditingFAQ(faq);
    setShowModal(true);
  };

  const closeModal = () => {
    setShowModal(false);
    setEditingFAQ(null);
  };

  const handleToggleStatus = (faq) => {
    setTogglingId(faq.id);
    toggleStatus(faq.id, {
      onSettled: () => setTogglingId(null),
    });
  };

  const columns = [
    {
      key: 'question',
      label: 'Question',
      sortable: true,
      width: 'w-2/5',
      render: (faq) => (
        <div className="min-w-0 max-w-md">
          <p className="text-sm font-medium text-gray-900 dark:text-gray-50 line-clamp-2" title={faq.question}>
            {faq.question || 'N/A'}
          </p>
          <p className="text-xs text-gray-500 dark:text-gray-400 line-clamp-1 mt-0.5" title={faq.answer}>
            {faq.answer || ''}
          </p>
        </div>
      ),
    },
    {
      key: 'category',
      label: 'Category',
      sortable: true,
      width: 'w-32',
      render: (faq) => (
        <span className="text-sm text-gray-700 dark:text-gray-300">{formatCategory(faq.category)}</span>
      ),
    },
    {
      key: 'order',
      label: 'Order',
      sortable: true,
      width: 'w-20',
      render: (faq) => (
        <span className="text-sm font-medium text-gray-900 dark:text-gray-50 tabular-nums">{faq.order ?? 0}</span>
      ),
    },
    {
      key: 'is_active',
      label: 'Status',
      sortable: true,
      width: 'w-36',
      render: (faq) => (
        <div
          className="flex items-center gap-2"
          onClick={(e) => e.stopPropagation()}
        >
          <span
            className={`inline-flex px-2 py-0.5 rounded-full text-xs font-medium ${
              faq.is_active
                ? 'bg-emerald-50 text-emerald-800 dark:bg-emerald-950/40 dark:text-emerald-300'
                : 'bg-gray-100 text-gray-600 dark:bg-gray-800 dark:text-gray-400'
            }`}
          >
            {faq.is_active ? 'Active' : 'Inactive'}
          </span>
          <PillToggle
            checked={Boolean(faq.is_active)}
            onChange={() => handleToggleStatus(faq)}
            loading={togglingId === faq.id && isToggling}
            disabled={togglingId === faq.id && isToggling}
            variant={faq.is_active ? 'success' : 'amber'}
            onLabel="On"
            offLabel="Off"
            ariaLabel={`Toggle FAQ: ${faq.question}`}
          />
        </div>
      ),
    },
    {
      key: 'created_at',
      label: 'Created',
      sortable: true,
      width: 'w-28',
      render: (faq) => (
        <span className="text-sm text-gray-600 dark:text-gray-400 whitespace-nowrap">
          {formatDate(faq.created_at, 'medium')}
        </span>
      ),
    },
  ];

  const actions = [
    {
      label: 'Edit',
      icon: Edit,
      variant: 'default',
      onClick: openEdit,
    },
    {
      label: 'Delete',
      icon: Trash2,
      variant: 'danger',
      onClick: (faq) => {
        if (window.confirm('Are you sure you want to delete this FAQ?')) {
          deleteFAQ(faq.id);
        }
      },
    },
  ];

  if (error) {
    return (
      <div className="space-y-5">
        <div className="rounded-2xl border border-red-200 dark:border-red-800/50 bg-red-50/80 dark:bg-red-950/30 p-8 text-center">
          <XCircle className="w-12 h-12 text-red-400 mx-auto mb-4" />
          <h3 className="text-lg font-medium text-red-800 dark:text-red-200 mb-2">Error Loading FAQs</h3>
          <p className="text-red-600 dark:text-red-300 text-sm">
            {error?.response?.data?.message || 'Failed to load FAQs. Please try again later.'}
          </p>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-5">
      <AdminPageHeader
        title="FAQ Management"
        subtitle="Manage frequently asked questions and help content."
        actions={
          <Button onClick={openCreate} className="!rounded-xl flex items-center gap-2">
            <Plus className="w-4 h-4" />
            Create FAQ
          </Button>
        }
      />

      <AdminGuideCallout
        title={ADMIN_PAGE_GUIDES.faqs.title}
        steps={ADMIN_PAGE_GUIDES.faqs.steps}
        icon={ListChecks}
      />

      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-3">
        <div className="rounded-2xl border border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-900 shadow-sm p-4 sm:p-5">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm text-gray-600 dark:text-gray-400">Total FAQs</p>
              <p className="text-2xl font-bold text-gray-900 dark:text-gray-50 mt-1 tabular-nums">{stats.total_faqs || 0}</p>
            </div>
            <BookOpen className="w-8 h-8 text-gray-400" />
          </div>
        </div>
        <div className="rounded-2xl border border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-900 shadow-sm p-4 sm:p-5">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm text-gray-600 dark:text-gray-400">Active</p>
              <p className="text-2xl font-bold text-emerald-600 dark:text-emerald-400 mt-1 tabular-nums">{stats.active_faqs || 0}</p>
            </div>
            <CheckCircle className="w-8 h-8 text-emerald-500" />
          </div>
        </div>
        <div className="rounded-2xl border border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-900 shadow-sm p-4 sm:p-5">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm text-gray-600 dark:text-gray-400">Total Views</p>
              <p className="text-2xl font-bold text-sky-600 dark:text-sky-400 mt-1 tabular-nums">{stats.total_views || 0}</p>
            </div>
          </div>
        </div>
        <div className="rounded-2xl border border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-900 shadow-sm p-4 sm:p-5">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm text-gray-600 dark:text-gray-400">Helpful Votes</p>
              <p className="text-2xl font-bold text-emerald-600 dark:text-emerald-400 mt-1 tabular-nums">{stats.total_helpful || 0}</p>
            </div>
            <CheckCircle className="w-8 h-8 text-emerald-500" />
          </div>
        </div>
      </div>

      <div className="rounded-2xl border border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-900 shadow-sm overflow-hidden">
        <div className="p-4 sm:p-5 space-y-5">
          <div className="rounded-xl border border-gray-200 dark:border-gray-700 bg-gray-50/50 dark:bg-gray-950/40 p-4">
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-3">
              <SearchSelect
                label="Status"
                value={filters.is_active}
                onChange={(value) => handleFilterChange('is_active', value)}
                options={activeStatusOptions}
              />
            </div>
          </div>

          {isLoading ? (
            <div className="py-20 text-center">
              <Loader2 className="w-8 h-8 animate-spin text-primary-600 mx-auto mb-3" />
              <p className="text-sm text-gray-500 dark:text-gray-400">Loading FAQs…</p>
            </div>
          ) : (
            <DataTable
              data={faqs}
              columns={columns}
              actions={actions}
              isLoading={false}
              onRowClick={openEdit}
              searchPlaceholder="Search by question, answer, or category..."
              onSearch={handleSearch}
              emptyMessage="No FAQs found"
              pageSize={10}
              className="!border-0 !shadow-none"
            />
          )}
        </div>
      </div>

      <AdminFAQModal
        isOpen={showModal}
        onClose={closeModal}
        faq={editingFAQ}
        createFAQ={createFAQ}
        updateFAQ={updateFAQ}
        isCreating={isCreating}
        isUpdating={isUpdating}
        existingFaqs={faqs}
      />
    </div>
  );
};

export default AdminFAQ;
